import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCheck, MessageCircle } from "lucide-react";

export default function FAQChat() {
  const { t } = useTranslation();
  const items = t("home.teaserFaq", { returnObjects: true }) || [];

  return (
    <section
      className="relative py-20 md:py-28 overflow-hidden"
      data-testid="faq-chat"
      style={{
        background:
          "radial-gradient(900px 500px at 18% 30%, rgba(47,122,58,0.08), transparent 60%), radial-gradient(700px 380px at 82% 70%, rgba(15,23,42,0.05), transparent 60%)",
      }}
    >
      <div className="max-w-[1180px] mx-auto px-5">
        <div className="grid lg:grid-cols-[1fr_640px] gap-12 lg:gap-16 items-start">
          {/* LEFT — Sticky title + WhatsApp CTA */}
          <div className="lg:sticky lg:top-12">
            <span className="inline-flex items-center gap-2 pill px-3 py-1.5 bg-velcor-green/8 border border-velcor-green/20 text-velcor-green text-[11px] font-black uppercase tracking-[0.14em] mb-5">
              <MessageCircle className="w-3.5 h-3.5" />
              {t("home.faqChatKicker")}
            </span>
            <h2 className="display text-[40px] md:text-[60px] text-velcor-ink leading-[1.02] mb-5">
              {t("home.faqChatTitle")} <span className="display-accent">{t("home.faqChatAccent")}</span>
            </h2>
            <p className="text-slate-600 text-[15.5px] leading-relaxed max-w-[420px]">
              {t("home.faqChatLead")}
            </p>
          </div>

          {/* RIGHT — Chat thread */}
          <div className="relative">
            {/* Phone-like frame */}
            <div className="relative rounded-[36px] bg-gradient-to-b from-slate-50 to-white border border-slate-200 shadow-[0_30px_80px_rgba(15,23,42,0.10)] overflow-hidden">
              {/* Chat header */}
              <div className="flex items-center gap-3 px-5 py-4 bg-white border-b border-slate-100">
                <img
                  src="/velcor-logo.png"
                  alt="Velcor"
                  className="w-10 h-10 object-contain"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-black text-velcor-ink text-[14px] leading-tight">
                    Velcor Energy
                  </div>
                  <div className="flex items-center gap-1.5 text-[11.5px] font-bold text-velcor-green mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-velcor-green animate-pulse" />
                    {t("home.faqChatOnline")}
                  </div>
                </div>
              </div>

              {/* Thread body */}
              <div className="px-4 sm:px-6 py-6 space-y-5 max-h-[680px] overflow-y-auto" data-testid="faq-chat-thread">
                {items.map((it, i) => (
                  <div key={i} className="space-y-2">
                    {/* Customer bubble (left, gray) */}
                    <motion.div
                      initial={{ opacity: 0, x: -16 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.35, delay: i * 0.04 }}
                      className="flex justify-start"
                      data-testid={`faq-chat-question-${i}`}
                    >
                      <div className="max-w-[82%] bg-slate-100 text-velcor-ink rounded-[20px] rounded-bl-md px-4 py-3 text-[14px] leading-snug font-bold">
                        {it.q}
                      </div>
                    </motion.div>
                    {/* Velcor bubble (right, green) */}
                    <motion.div
                      initial={{ opacity: 0, x: 16 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.4, delay: i * 0.04 + 0.12 }}
                      className="flex justify-end"
                      data-testid={`faq-chat-answer-${i}`}
                    >
                      <div className="max-w-[86%] bg-gradient-to-br from-velcor-green to-velcor-green/90 text-white rounded-[20px] rounded-br-md px-4 py-3 text-[13.5px] leading-relaxed shadow-[0_8px_22px_rgba(47,122,58,0.18)]">
                        {it.a}
                        <div className="flex items-center justify-end gap-1 mt-2 text-white/70 text-[10.5px] font-bold">
                          <span>{i % 2 === 0 ? "10:31" : "10:32"}</span>
                          <CheckCheck className="w-3.5 h-3.5" />
                        </div>
                      </div>
                    </motion.div>
                  </div>
                ))}

                {/* Typing indicator at the end (subtle) */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                  className="flex justify-start"
                >
                  <div className="bg-slate-100 rounded-full px-4 py-2.5 inline-flex items-center gap-1">
                    {[0, 1, 2].map((d) => (
                      <span
                        key={d}
                        className="w-1.5 h-1.5 rounded-full bg-slate-400 inline-block faq-chat-dot"
                        style={{ animationDelay: `${d * 0.18}s` }}
                      />
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Helper line under thread */}
            <p className="mt-4 text-center text-slate-500 text-[12.5px] font-bold">
              {t("home.faqChatHelper")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
